import type { MtoMaterialPosition, NsiMapping } from '@entities/mto';
import {
  fmtNum,
  fmtQtyText,
  needsCheck,
  positionRaw,
  systemShareLines,
} from '@entities/mto';
import { nsiOf } from '../helpers';
import {
  Muted,
  Name,
  Small,
  Source,
  Table,
  TableScroll,
  Tr,
} from '../styles';
import type { Tab } from '../types';
import { TAB_LABEL } from '../types';
import { MultNote } from './MultNote';
import { ReviewReasons } from './ReviewReasons';
import { RowDecision } from './RowDecision';
import { SystemShares } from './SystemShares';

export function MaterialsTable({
  rows,
  shown,
  activeTab,
  withSystems,
  nsiByPosition,
  runId,
  canWrite,
  onRowError,
}: {
  rows: MtoMaterialPosition[];
  shown: number;
  activeTab: Tab;
  withSystems: boolean;
  nsiByPosition: Map<string, NsiMapping>;
  runId: string;
  canWrite: boolean;
  onRowError: (msg: string) => void;
}) {
  return (
    <TableScroll tabIndex={0} role="region" aria-label={TAB_LABEL[activeTab]}>
      <Table>
        <thead>
          <tr>
            <th scope="col">Наименование</th>
            <th scope="col">Ед.</th>
            <th scope="col" className="num">
              Кол-во
            </th>
            {activeTab === 'materials' && (
              <th scope="col" className="num">
                Вес, кг
              </th>
            )}
            {activeTab === 'materials' && withSystems && (
              <th scope="col">По системам</th>
            )}
            <th scope="col">Код 1С</th>
            <th scope="col">Проверка</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, shown).map((p) => {
            const r = positionRaw(p);
            const nsi = nsiOf(nsiByPosition.get(p.id));
            const excluded = p.decision === 'rejected';
            const details = [p.designation, p.gost, p.code].filter(Boolean).join(' · ');
            return (
              <Tr key={p.id} $review={needsCheck(p)}>
                <td>
                  <Name $excluded={excluded}>{p.name}</Name>
                  {details && <Small>{details}</Small>}
                  {r.name_variants && r.name_variants.length > 0 && (
                    <Small>
                      Тот же код, также записано:{' '}
                      {r.name_variants.map((n) => `«${n}»`).join(', ')}
                    </Small>
                  )}
                  {(r.per_unit || r.multiplicity) && (
                    <Small>
                      на 1 элемент — в итоги не входит
                      {r.quantity_mult != null ? '; в сверку — с кратностью' : ''}
                    </Small>
                  )}
                  {r.rebar_bar && <Small>стержень — масса учтена в своде арматуры</Small>}
                  <Source title={p.source_ref}>{p.source_ref}</Source>
                  <ReviewReasons reasons={needsCheck(p) ? (r.review_reasons ?? []) : []} />
                </td>
                <td>{p.unit ?? '—'}</td>
                <td className="num">
                  {fmtQtyText(p.quantity)}
                  <MultNote
                    m={r.multiplicity}
                    manual={r.multiplicity_manual}
                    total={r.quantity_mult != null ? fmtQtyText(r.quantity_mult) : null}
                  />
                </td>
                {activeTab === 'materials' && (
                  <td className="num">
                    {fmtNum(r.total_weight)}
                    {r.unit_mass != null && <Small>{fmtNum(r.unit_mass)} кг/ед.</Small>}
                  </td>
                )}
                {activeTab === 'materials' && withSystems && (
                  <td>
                    <SystemShares lines={systemShareLines(r)} />
                  </td>
                )}
                <td>
                  {nsi ? (
                    <>
                      <Name>{nsi.code ?? '—'}</Name>
                      <Small>{nsi.name}</Small>
                    </>
                  ) : (
                    <Muted>—</Muted>
                  )}
                </td>
                <td>
                  <RowDecision
                    position={p}
                    runId={runId}
                    canWrite={canWrite}
                    onError={onRowError}
                  />
                </td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
    </TableScroll>
  );
}
